/**
 * DevicesTab Component
 *
 * Tab for viewing and managing user push notification devices.
 * Features: view device list, delete devices (single or bulk), activate/deactivate devices.
 */

import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { IconDevices, IconTrash, IconRefresh, IconPlayerPause, IconPlayerPlay, IconAlertCircle } from '@tabler/icons-react';
import {
  Card,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Alert,
  ScrollArea,
  Modal,
  Text,
  Checkbox,
  EmptyState,
} from '../../../../components/ui';
import { useHttpClient } from '@actschurch/ccms-sdk';
import type { BulkDeleteResult } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import type { DevicesTabProps, ResponseData } from '../types';

export function DevicesTab({
  userId,
  devices,
  onDeviceDelete,
  onDeviceToggle,
  onRefresh,
  loading = false,
}: DevicesTabProps) {
  const httpClient = useHttpClient();
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteOpened, setBulkDeleteOpened] = useState(false);

  const selectedDevices = devices.filter((device) => selectedIds.includes(device.id));
  const allSelected = devices.length > 0 && selectedIds.length === devices.length;

  // Drop selections for devices that are no longer in the list (deleted elsewhere or refreshed away).
  useEffect(() => {
    setSelectedIds((current) => {
      const stillPresent = current.filter((id) => devices.some((device) => device.id === id));
      return stillPresent.length === current.length ? current : stillPresent;
    });
  }, [devices]);

  const toggleSelected = (deviceId: string, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? [...current, deviceId] : current.filter((id) => id !== deviceId)
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? devices.map((device) => device.id) : []);
  };

  /**
   * Format timestamp
   */
  const formatTimestamp = (timestamp: number | null | undefined) => {
    if (!timestamp) return 'Never';

    const date = new Date(timestamp * 1000); // Assuming Unix timestamp
    return date.toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Handle delete device confirmation
   */
  const handleDeleteDevice = (deviceId: string) => {
    setDeviceToDelete(deviceId);
    setDeleteConfirmOpened(true);
  };

  /**
   * Confirm delete device
   */
  const confirmDeleteDevice = async () => {
    if (!deviceToDelete) return;

    setActionLoading(true);
    setError(null);

    try {
      const url = getApiUrl('users', `/api/userpushdevice/${deviceToDelete}`);
      await httpClient.delete<ResponseData<boolean>>(url);

      notifications.show({
        title: 'Success',
        message: 'Device deleted successfully',
        color: 'green',
      });

      setDeleteConfirmOpened(false);
      setDeviceToDelete(null);
      await onDeviceDelete(deviceToDelete);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete device';
      setError(errorMessage);
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Confirm delete of every selected device. Sends the whole selection as ONE request — a user with
   * 100+ devices would otherwise wait on 100 sequential round-trips.
   */
  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    setActionLoading(true);
    setError(null);

    try {
      const url = getApiUrl('users', '/api/userpushdevice/bulk-delete');
      const response = await httpClient.post<ResponseData<BulkDeleteResult>>(url, {
        ids: selectedIds.map(Number),
      });
      const result = (response as any).data?.data ?? (response as any).data;

      const deleted = result?.deleted ?? selectedIds.length;
      const notFound = result?.notFoundIds?.length ?? 0;

      notifications.show({
        title: 'Success',
        message:
          notFound > 0
            ? `Deleted ${deleted} device(s); ${notFound} were already gone`
            : `${deleted} ${deleted === 1 ? 'device' : 'devices'} deleted successfully`,
        color: 'green',
      });

      setBulkDeleteOpened(false);
      setSelectedIds([]);

      try {
        await onRefresh();
      } catch {
        setError('Devices were deleted but the list could not be refreshed. Please refresh.');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete devices';
      setError(errorMessage);
      notifications.show({ title: 'Error', message: errorMessage, color: 'red' });
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle toggle device active status
   */
  const handleToggleDevice = async (deviceId: string, activate: boolean) => {
    setActionLoading(true);
    setError(null);

    try {
      const endpoint = activate ? 'activate' : 'deactivate';
      const url = getApiUrl('users', `/api/userpushdevice/${deviceId}/${endpoint}`);
      await httpClient.post<ResponseData<boolean>>(url, {});

      notifications.show({
        title: 'Success',
        message: `Device ${activate ? 'activated' : 'deactivated'} successfully`,
        color: 'green',
      });

      await onDeviceToggle(deviceId, activate);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to toggle device';
      setError(errorMessage);
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle refresh devices
   */
  const handleRefresh = async () => {
    setError(null);
    try {
      await onRefresh();
      notifications.show({
        title: 'Refreshed',
        message: 'Device list refreshed',
        color: 'blue',
      });
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to refresh devices';
      setError(errorMessage);
    }
  };

  return (
    <Stack gap="md">
      {/* Error Alert */}
      {error && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title="Error"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Devices Card */}
      <Card withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                Registered Devices
              </Text>
              <Text size="xs" c="dimmed">
                Push notification devices registered by this user
              </Text>
            </div>
            <Group gap="xs">
              <Badge size="lg" variant="light" color="blue">
                {devices.length} {devices.length === 1 ? 'Device' : 'Devices'}
              </Badge>
              {selectedIds.length > 0 && (
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconTrash size={16} />}
                  onClick={() => setBulkDeleteOpened(true)}
                  disabled={actionLoading}
                  size="sm"
                >
                  Delete Selected ({selectedIds.length})
                </Button>
              )}
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={loading}
                size="sm"
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {/* Devices Table */}
          {devices.length > 0 ? (
            <ScrollArea>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 40 }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedIds.length > 0 && !allSelected}
                        onChange={(event) => toggleSelectAll(event.currentTarget.checked)}
                        aria-label="Select all devices"
                      />
                    </Table.Th>
                    <Table.Th style={{ width: 80 }}>ID</Table.Th>
                    <Table.Th>Device Name</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Platform</Table.Th>
                    <Table.Th>App Version</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Last Updated</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {devices.map((device) => (
                    <Table.Tr key={device.id}>
                      <Table.Td>
                        <Checkbox
                          checked={selectedIds.includes(device.id)}
                          onChange={(event) => toggleSelected(device.id, event.currentTarget.checked)}
                          aria-label={`Select device #${device.id}`}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          #{device.id}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={500}>{device.deviceName || 'Unknown Device'}</Text>
                        <Text size="xs" c="dimmed">
                          {device.deviceId || 'No ID'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{device.deviceType || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{device.platform || '-'}</Text>
                        {device.osVersion && (
                          <Text size="xs" c="dimmed">
                            {device.osVersion}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{device.appVersion || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={device.isActive ? 'green' : 'gray'} variant="light">
                          {device.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {formatTimestamp(device.lastUpdated)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <ActionIcon
                            variant="light"
                            color={device.isActive ? 'orange' : 'green'}
                            onClick={() => handleToggleDevice(device.id, !device.isActive)}
                            disabled={actionLoading}
                            title={device.isActive ? 'Deactivate device' : 'Activate device'}
                          >
                            {device.isActive ? (
                              <IconPlayerPause size={16} />
                            ) : (
                              <IconPlayerPlay size={16} />
                            )}
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleDeleteDevice(device.id)}
                            disabled={actionLoading}
                            title="Delete device"
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          ) : (
            <EmptyState
              icon={<IconDevices size={64} />}
              title="No devices registered"
              description="This user has not registered any push notification devices."
              size="md"
            />
          )}
        </Stack>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteConfirmOpened}
        onClose={() => setDeleteConfirmOpened(false)}
        title="Confirm Delete Device"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this device? The user will no longer receive push
            notifications on this device.
          </Text>

          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setDeleteConfirmOpened(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDeleteDevice} loading={actionLoading}>
              Delete Device
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        opened={bulkDeleteOpened}
        onClose={() => setBulkDeleteOpened(false)}
        title={`Delete ${selectedIds.length} ${selectedIds.length === 1 ? 'Device' : 'Devices'}?`}
        centered
      >
        <Stack gap="md">
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="This cannot be undone">
            {selectedIds.length} selected {selectedIds.length === 1 ? 'device' : 'devices'} will be
            removed permanently. The user will no longer receive push notifications on{' '}
            {selectedIds.length === 1 ? 'it' : 'them'}.
          </Alert>

          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            <Stack gap={4}>
              {selectedDevices.map((device) => (
                <Text key={device.id} size="sm">
                  #{device.id} — {device.deviceName || 'Unknown Device'}
                  {device.platform ? ` (${device.platform})` : ''}
                </Text>
              ))}
            </Stack>
          </div>

          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setBulkDeleteOpened(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmBulkDelete} loading={actionLoading}>
              Delete {selectedIds.length} {selectedIds.length === 1 ? 'Device' : 'Devices'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
